import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import React, {useEffect, useRef, useState} from 'react';
import {Card, Col, Container, Row} from 'react-bootstrap';
import {FaHeartbeat, FaSyringe, FaVial} from 'react-icons/fa';
import {useDispatch, useSelector} from 'react-redux';
import {DateRangePicker} from 'rsuite';
import Swal from 'sweetalert2';
import {getAnalysis} from '../../../Redux/Thunk/fertiltyActions';
import BarChart from './BarChart';
import PieChart from './PieChart';

const iconMap = {
  ivf: <FaVial size={24} />,
  iui: <FaHeartbeat size={24} />,
  oi: <FaSyringe size={24} />
};

const Dashboard = ({loaderchange, popupalert, popuptext}) => {
  const printRef = useRef();

  const [filters, setFilters] = useState({});
  const [reportData, setReportData] = useState({});
  const [value, setValue] = useState(null);

  const sideactive = useSelector(state => state.counter.sidebarnav);
  const dispatch = useDispatch();

  useEffect(() => {
    fetchAnalytics();
  }, []);

  useEffect(() => {
    const from = filters.beta_hcg_test_date_from;
    const to = filters.beta_hcg_test_date_to;

    if (from && to) {
      fetchAnalytics();
    }
  }, [filters]);

  const handleDateChange = newValue => {
    setValue(newValue);

    if (newValue?.length === 2) {
      const [start, end] = newValue;

      const startDate = new Date(start);
      const endDate = new Date(end);

      startDate.setHours(0, 0, 0, 0);

      endDate.setHours(23, 59, 59, 999);

      const startIso = startDate.toISOString();
      const endIso = endDate.toISOString();

      setFilters(prev => ({
        ...prev,
        beta_hcg_test_date_from: startIso,
        beta_hcg_test_date_to: endIso
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        beta_hcg_test_date_from: '',
        beta_hcg_test_date_to: ''
      }));
    }
    if (filters.beta_hcg_test_date_from && filters.beta_hcg_test_date_to) fetchAnalytics();
  };

  const fetchAnalytics = async () => {
    const dataPayload = {
      beta_hcg_test_date_from: filters.beta_hcg_test_date_from || '',
      beta_hcg_test_date_to: filters.beta_hcg_test_date_to || ''
    };
    loaderchange('true'); // if loaderchange accepts boolean

    try {
      const response = await dispatch(getAnalysis(dataPayload)).unwrap();

      if (response?.status?.code === 200) {
        const reportData = response.data;
        setReportData(reportData);
      } else {
        Swal.fire({
          title: 'Error.',
          text: 'No Patient Found.',
          icon: 'warning',
          showConfirmButton: false,
          timer: 1500
        });
      }
    } catch (error) {
      console.error('Error fetching patient details:', error);
      Swal.fire({
        title: 'Error.',
        text: 'Failed to get patient details.',
        icon: 'error',
        showConfirmButton: false,
        timer: 1500
      });
    } finally {
      loaderchange('false');
    }
  };

  let totalPositive = 0;
  let totalNegative = 0;

  if (!reportData.ivf) return null;
  if (reportData.ivf) {
    totalPositive = reportData.ivf.ivf_positive_count + reportData.iui.iui_positive_count + reportData.oi.oi_positive_count;
    totalNegative = reportData.ivf.ivf_negative_count + reportData.iui.iui_negative_count + reportData.oi.oi_negative_count;
  }

  const handlePrint = () => {
    const input = printRef.current;

    // Hide no-print elements
    const noPrintElements = document.querySelectorAll('.no-print');
    noPrintElements.forEach(el => (el.style.display = 'none'));

    html2canvas(input, {scale: 2}).then(canvas => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save('dashboard.pdf');

      // Restore display after PDF is saved
      noPrintElements.forEach(el => (el.style.display = ''));
    });
  };

  return (
    <>
      <div ref={printRef} className={sideactive ? 'maincontainer-patient-sa' : 'maincontainer-patient'}>
        <Container className='py-0'>
          <div className='text-end my-2 no-print'>
            <button onClick={handlePrint} className='btn btn-primary'>
              Download PDF
            </button>
          </div>
          <header className='text-center mb-2'>
            <h2 className='fw-bold text-primary mb-3'>
              <i className='bi bi-bar-chart-line-fill me-3'></i>
              Treatment Analytics Dashboard
            </h2>
          </header>

          <Row className='g-4 mb-3 no-print'>
            <div className='row'>
              <div className='col-3 col-md-3'>
                <DateRangePicker
                  value={value}
                  onChange={handleDateChange}
                  placeholder='Beta-hCG Date Range'
                  appearance='default'
                  className='w-100 custom-date-picker'
                  format='dd-MM-yyyy'
                  shouldDisableDate={date => false}
                />
              </div>
            </div>
          </Row>
          <Row className='g-4 mb-0'>
            {['ivf', 'iui', 'oi'].map(key => (
              <Col md={4} key={key}>
                <Card className='shadow-sm h-80 border-0'>
                  <Card.Body className='d-flex flex-column'>
                    <div className='d-flex align-items-center mb-3'>
                      <div className='bg-primary p-3 rounded-circle me-3'>{React.cloneElement(iconMap[key], {className: 'text-white'})}</div>
                      <h5 className='mb-0 text-uppercase fw-bold'>{key} Summary</h5>
                    </div>
                    <div className='mt-auto'>
                      <Row className='text-center'>
                        <Col>
                          <div className='text-muted small'>Total</div>
                          <div className='h3'>{reportData[key][`${key}_count`]}</div>
                        </Col>
                        <Col>
                          <div className='text-success small'>Positive</div>
                          <div className='h3'>{reportData[key][`${key}_positive_count`]}</div>
                        </Col>
                        <Col>
                          <div className='text-danger small'>Negative</div>
                          <div className='h3'>{reportData[key][`${key}_negative_count`]}</div>
                        </Col>
                      </Row>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <Row className='g-4'>
            <Col xl={6} className='d-flex flex-column'>
              <Card className='shadow-sm border-0 flex-fill'>
                <Card.Body>
                  <h5 className='card-title mb-4'>Treatment Outcomes by Type</h5>
                  <div className='chart-container' style={{height: '400px'}}>
                    <BarChart data={reportData} />
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xl={6} className='d-flex flex-column'>
              <Card className='shadow-sm border-0 flex-fill'>
                <Card.Body className='d-flex flex-column'>
                  <h5 className='card-title mb-4'>Overall Success Rate</h5>
                  <div className='chart-container flex-fill' style={{position: 'relative', height: '300px'}}>
                    <PieChart positive={totalPositive} negative={totalNegative} />
                  </div>
                  <div className='mt-4 text-center'>
                    <Row>
                      <Col>
                        <div className='text-success h4 mb-0'>{totalPositive}</div>
                        <small className='text-muted'>Positive</small>
                      </Col>
                      <Col>
                        <div className='text-danger h4 mb-0'>{totalNegative}</div>
                        <small className='text-muted'>Negative</small>
                      </Col>
                    </Row>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default Dashboard;
